import { IonRouterLink } from '@ionic/react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ActionIconButton from '@components/buttons/ActionIconButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import DateStamp from '@components/dates/DateStamp';
import TimeRange from '@components/dates/TimeRange';
import DropdownButton from '@components/DropdownButton';
import styles from '@components/expert/ExpertConsultsPage.module.css';
import Tag from '@components/labels/Tag';
import Nbsp from '@components/Nbsp';
import TextWithIcon from '@components/text/TextWithIcon';
import TextWithAvatar from '@components/TextWithAvatar';
import useMyExpert from '@hooks/useMyExpert';
import { ConsultTimePeriodEnum, ExpertGlobalRoleEnum, GetExpertConsultsByAttributeQuery } from '@typing/Generated';
import { ConsultReportValues } from '@typing/Models';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { languageNameForCode } from '@utils/languageUtils';
import { journeyRoute } from '@utils/routeUtils';

type ConsultType = NonNullable<
  NonNullable<NonNullable<GetExpertConsultsByAttributeQuery['firstConsults']['edges']>[number]>['node']
>;

type Props = {
  consult: ConsultType;
  consultReportOptions: {
    name: string;
    value: ConsultReportValues;
  }[];
  date: Date;
  dateByString: (date: Date) => string;
  onReassign: (consult: ConsultType) => void;
  upcoming?: ConsultTimePeriodEnum | null;
};

const ExpertConsultRow = ({ consult, consultReportOptions, date, dateByString, onReassign, upcoming }: Props) => {
  const [consultReportValue, setConsultReportValue] = useState(
    consult.consultReport?.published ? ConsultReportValues.CONSULT_REPORT : ConsultReportValues.INITIAL_ASSESSMENT
  );
  const myExpert = useMyExpert();
  const intl = useIntl();

  const handleConsultReportValueSelect = useCallback((value: string) => {
    setConsultReportValue(value as ConsultReportValues);
  }, []);

  const colorForExpertTag = consult.expert.id === consult.journey.primaryExpert?.id ? 'primary300' : 'secondary500';

  return (
    <tr key={consult.id}>
      <td>
        <div className={styles.bigText}>
          <div>{dateByString(consult.startTime)}</div>
          <div>
            <DateStamp showWeekday={false} value={consult.startTime} />
          </div>
        </div>
      </td>
      <td>
        <div
          className={
            upcoming === ConsultTimePeriodEnum.upcoming && consult.endTime < date ? styles.disabled : undefined
          }
        >
          <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
        </div>
      </td>
      <td>
        {consult.journey.owner.lastName}
        <FormattedMessage id="dictionary.punctuation.comma" />
        <Nbsp />
        {consult.journey.owner.firstName}
      </td>
      <td>
        <div className={styles.language}>
          {consult.languageCode && (
            <TextWithIcon icon="commentAltPlus" iconFirst text={languageNameForCode(consult.languageCode)} />
          )}
        </div>
      </td>
      <td>
        <IonRouterLink
          className={styles.bigText}
          routerLink={journeyRoute({ journeyId: consult.journey.id, linkAfterJourney: '', myExpert })}
        >
          {consult.careProfileName}
        </IonRouterLink>
      </td>
      <td>
        <div className={styles.roles}>
          {myExpert?.id === consult.expert.id ? (
            <Tag
              color={colorForExpertTag}
              text={intl.formatMessage(
                {
                  id: `pages.expert.myConsult`
                },
                {
                  firstName: consult.expert.firstName
                }
              )}
            />
          ) : (
            <TextWithAvatar
              color={colorForExpertTag}
              photoUrl={consult.expert.photoUrl}
              text={consult.expert.firstName}
            />
          )}
        </div>
      </td>
      <td>
        {consult.journey.initialConsultReport.consult?.id === consult.id ? (
          <div className={styles.buttonWidth}>
            {consult.journey.initialConsultReport.published ? (
              <SecondaryButton
                expand="block"
                routerLink={journeyRoute({
                  journeyId: consult.journey.id,
                  linkAfterJourney: `/initial-consult-report`,
                  myExpert
                })}
                textKey="models.consultReport.reportOptions.initial_assessment"
              />
            ) : (
              <PrimaryButton
                data-test="initial-assessment-button"
                expand="block"
                onKeyUp={clickCurrentTarget}
                routerLink={journeyRoute({
                  journeyId: consult.journey.id,
                  linkAfterJourney: `/initial-consult-report/edit/${consult.id}`,
                  myExpert
                })}
                tabIndex={0}
                textKey="models.consultReport.reportOptions.initial_assessment"
              />
            )}
          </div>
        ) : !consult.journey.initialConsultReport.consult && !consult.consultReport?.published ? (
          <DropdownButton
            options={consultReportOptions}
            routerLink={journeyRoute({
              journeyId: consult.journey.id,
              linkAfterJourney: `/consults/${consult.id}/${
                consultReportValue === ConsultReportValues.INITIAL_ASSESSMENT
                  ? 'initial-consult-report/edit'
                  : 'consult-report'
              }`,
              myExpert
            })}
            setValue={handleConsultReportValueSelect}
            value={consultReportValue}
          >
            <FormattedMessage id={`models.consultReport.reportOptions.${consultReportValue}`} />
          </DropdownButton>
        ) : (
          <div className={styles.buttonWidth}>
            {consult.consultReport?.published ? (
              <SecondaryButton
                expand="block"
                routerLink={journeyRoute({
                  journeyId: consult.journey.id,
                  linkAfterJourney: `/consults/${consult.id}/consult-report`,
                  myExpert
                })}
                textKey="models.consultReport.reportOptions.consult_report"
              />
            ) : (
              <PrimaryButton
                expand="block"
                routerLink={journeyRoute({
                  journeyId: consult.journey.id,
                  linkAfterJourney: `/consults/${consult.id}/consult-report`,
                  myExpert
                })}
                textKey="models.consultReport.reportOptions.consult_report"
              />
            )}
          </div>
        )}
      </td>
      {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager && consult.endTime > new Date() && (
        <td>
          <ActionIconButton
            ariaLabelKey="accessibility.ariaLabels.reassign"
            hoverContent={<FormattedMessage id="dictionary.reassign" />}
            icon="peopleArrows"
            onClick={() => onReassign(consult)}
          />
        </td>
      )}
    </tr>
  );
};

export default ExpertConsultRow;
